import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Form } from 'app/shared/form';
import { HttpErrorHandler } from '../../shared/http-error-handler';

import { Document } from 'app/_models/document';
import { DocumentService } from 'app/_services/document.service';
import { Entity } from 'app/_models/entity';
import { Ship } from 'app/_models/ship';
import { EntityService } from 'app/_services/entity.service';
import { ShipService } from 'app/_services/ship.service';

@Component({
  selector: 'app-document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.scss'],
})
export class DocumentFormComponent extends Form implements OnInit {
  form: FormGroup;
  document: Document;

  buyers: Entity[] = [];
  ships: Ship[] = [];

  selectedFile: File | null = null;
  msg_error: String;
  isDragOver = false;
  today: string;

  constructor(
    private fb: FormBuilder,
    private documentService: DocumentService,
    private entityService: EntityService,
    private shipService: ShipService,
    protected route: ActivatedRoute,
    protected router: Router,
    private location: Location,
    protected httpErrorHandler: HttpErrorHandler
  ) {
    super(route, httpErrorHandler);
    const today = new Date().toISOString().split('T')[0];

    this.form = this.fb.group({
      id: [null],
      document_file: [null, [Validators.required, this.fileValidator.bind(this)]],
      document_type: ['', Validators.required],
      day: [today, Validators.required],
      buyer_entity: [null, Validators.required],
      ship: [null],
    });
  }

  ngOnInit() {
    this.today = new Date().toISOString().split('T')[0];
    this.loadData();

    if (this.location.path() === '/documents/add' || this.location.path() === '/documents/add/') {
      this.document = new Document();
      this.setFormMode('create');
    } else {
      this.getDocument();
      this.setFormMode('update');
    }

    const documentTypeControl = this.form.get('document_type');
    const shipControl = this.form.get('ship');

    if (documentTypeControl && shipControl) {
      documentTypeControl.valueChanges.subscribe((value) => {
        if (value === 'iccat') {
          shipControl.setValidators([Validators.required]);
        } else {
          shipControl.clearValidators();
          shipControl.setValue(null);
        }
        shipControl.updateValueAndValidity();
      });
    }
  }

  async loadData() {
    try {
      this.getBuyers(), this.getShips();
    } catch (error) {
      console.error('Error loading initial data: ', error);
      this.httpErrorHandler.handleHttpError(error);
    }
  }

  getBuyers() {
    this.entityService.list(1, 10000, '&type=c&type=cv', '').subscribe(
      (response) => {
        this.buyers = this.parseDropdownResults(response, new Entity(), 'name', true);
      },
      (error) => this.httpErrorHandler.handleHttpError(error)
    );
  }
  getShips() {
    this.shipService.list(1, 10000, '', '').subscribe(
      (response) => {
        this.ships = this.parseDropdownResults(response.results, new Ship(), 'name', true);
      },
      (error) => this.httpErrorHandler.handleHttpError(error)
    );
  }

  getDocument() {
    this.route.params.forEach((params: Params) => {
      const id = +params['id'];
      this.documentService.getDocument(id).subscribe(
        (document) => {
          this.document = document;
        },
        (error) => this.handleError(error)
      );
    });
  }

  handleError(error) {
    if (error.status === 400 && error.body['non_field_errors']) {
      this.msg_error = error.body['non_field_errors'][0];
    } else {
      const errors = this.httpErrorHandler.handleHttpError(error);
      this.parseApiErrors(errors, this.form);
    }
  }

  onSubmit() {
    Object.keys(this.form.controls).forEach((key) => {
      // Mark each control as touched
      this.form.controls[key].markAsTouched();
    });
    if (this.form.invalid) {
      return;
    }
    const formValue = this.form.value;
    let formatedDate = '';
    if (formValue.day) {
      if (formValue.day instanceof Date) {
        formatedDate = formValue.day.toISOString().split('T')[0];
      } else if (typeof formValue.day === 'string') {
        formatedDate = formValue.day;
      }
    }
    if (formValue.ship) {
      this.document = new Document({
        ...formValue,
        day: formatedDate,
        buyer_entity_id: formValue.buyer_entity.id,
        ship_id: formValue.ship.id,
      });
    } else {
      this.document = new Document({
        ...formValue,
        day: formatedDate,
        buyer_entity_id: formValue.buyer_entity.id,
      });
    }

    if (this.createMode) {
      this.createDocument();
    } else {
      this.updateDocument();
    }
  }

  createDocument() {
    const formData = new FormData();
    formData.append('document_file', this.selectedFile, this.selectedFile.name);
    formData.append('document_type', this.form.value.document_type);
    formData.append('day', this.form.value.day);
    if (this.form.value.document_type === 'iccat') {
      formData.append('ship_id', this.form.value.ship.id);
    }
    formData.append('buyer_entity_id', this.form.value.buyer_entity.id);
    this.documentService.create(formData).subscribe(
      (doc) => this.onCreate(doc),
      (error) => this.handleError(error)
    );
  }

  updateDocument() {
    this.documentService.update(this.document).subscribe(
      (document) => this.onUpdate(document),
      (error) => this.handleError(error)
    );
  }

  onCreate(document: Document) {
    this.goBack();
  }

  onUpdate(document: Document) {
    this.goBack();
  }

  goBack(): void {
    this.location.back();
  }

  fileValidator(control: any) {
    if (!this.selectedFile) {
      return null;
    }

    const errors: any = {};

    if (!this.selectedFile.name.toLowerCase().endsWith('.pdf')) {
      errors.fileType = true;
    }

    if (this.selectedFile.size > 1 * 1024 * 1024) {
      errors.fileSize = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  private setSelectedFile(file: File | null): void {
    this.selectedFile = file;

    // Patch form control with a display value to trigger validation
    this.form.patchValue({
      document_file: this.selectedFile ? this.selectedFile.name : null,
    });

    // Re-run validators without optional chaining
    const ctrl = this.form.get('document_file');
    if (ctrl) {
      ctrl.updateValueAndValidity();
    }
  }

  // Input change handler without optional chaining
  // Extracts the first file safely and delegates to the centralized setter
  onFileChange(event: any): void {
    let file: File | null = null;
    if (event && event.target && event.target.files && event.target.files.length) {
      file = event.target.files[0];
    }
    this.setSelectedFile(file);
  }

  // Allow drop by preventing default and show visual feedback
  onDragOver(event: any): void {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    this.isDragOver = true;
  }

  // Remove visual feedback when dragging leaves the zone
  onDragLeave(event: any): void {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    this.isDragOver = false;
  }

  // Handle drop without optional chaining
  // Reads first file from DataTransfer and reuses the same validation flow
  onDrop(event: any): void {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    this.isDragOver = false;

    let file: File | null = null;
    const files = event && event.dataTransfer ? event.dataTransfer.files : null;
    if (files && files.length) {
      file = files[0];
    }
    this.setSelectedFile(file);
  }
}



// WEBPACK FOOTER //
// ./src/app/documents/document-form/document-form.component.ts